import React from 'react';
import {useSelector} from 'react-redux';
import './RecruitmentSummary.css';
import {
  selectCurrentPlayer,
  selectPhase,
  selectWarPhaseStage,
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {PHASES, WAR_PHASE_STAGES} from "../../../constants/phases";
import {useIsCurrentUserTurn} from "../../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import {calcPlayerUnitsAndSupply} from "../../../utils/gameState/recruitment/calcPlayerUnitsAndSupply";
import {
  checkIfConstructionPlanningAvailable,
  getConstructionPlanPlayer,
} from "../../../utils/gameState/construction/planning/getConstructionPlanContext";
import {GAME_PANEL_MATERIALS, GAME_PANEL_SHAPES, GamePanel} from '../../gameUi/GamePanel';

/**
 * Чьи числа показываем: обычно ходящего игрока, но при разметке плана в чужой
 * ход строительства — самого пользователя: план его, и смотрит он на свое
 * снабжение
 */
const selectSummaryPlayer = (state) => (
  checkIfConstructionPlanningAvailable(state) ? getConstructionPlanPlayer(state) : selectCurrentPlayer(state)
);

/**
 * Сводка этапа найма: сколько у игрока отрядов и сколько снабжения дадут все
 * его рабочие фермы. Нанимая, игрок сравнивает именно эти два числа — без них
 * приходится пересчитывать фермы по полю глазами.
 *
 * Место и оформление — как у плашки элементов у Элементалей в фазе
 * строительства (над кнопкой завершения фазы на десктопе, слева от нее на
 * телефоне, см. Footer).
 *
 * Видит ее только тот, чей сейчас ход (в хот-сите ход и игрок за экраном — одно
 * и то же, поэтому там плашка остается; у зрителей и в записи матча ее нет), и
 * планирующий строительство в чужой ход — но каждый только свои числа, чужой
 * расклад по отрядам и снабжению противнику показывать незачем.
 */
const RecruitmentSummary = () => {
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const summaryPlayer = useSelector(selectSummaryPlayer);
  const isCurrentUserTurn = useIsCurrentUserTurn();
  const isPlanningConstruction = useSelector(checkIfConstructionPlanningAvailable);
  // Тот же расчет, что и у подсказки после найма («кто-то будет голодать»,
  // см. recruitNewUnitOnBarracks): числа плашки и условие подсказки обязаны
  // совпадать. Селектор отдает числа, а не объект: новый объект на каждом
  // вызове перерисовывал бы плашку на любое изменение стора
  const unitsCount = useSelector((state) => calcPlayerUnitsAndSupply(state, selectSummaryPlayer(state)).unitsCount);
  const workingFarmsSupply = useSelector((state) => calcPlayerUnitsAndSupply(state, selectSummaryPlayer(state)).supply);

  if (
    !(
      (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.RECRUITMENT)
      || phase === PHASES.CONSTRUCTION
    )
    || !summaryPlayer || !(isCurrentUserTurn || isPlanningConstruction)
  ) {
    return null;
  }

  return (
    // Светлый жетон над полем (материал bone дизайн-системы матча)
    <GamePanel
      material={GAME_PANEL_MATERIALS.BONE}
      shape={GAME_PANEL_SHAPES.S}
      shadow={true}
      className={'recruitment-summary_shadow'}
    >
      <div className={'recruitment-summary'}>
      <img
        className={'recruitment-summary_icon recruitment-summary_icon-unit'}
        src={'/img/icons/unit d6 friend.png'}
        alt={'Отряды'}
        title={'Отрядов у игрока'}
      />
      <span className={'recruitment-summary_value'}>{unitsCount}</span>
      <img
        className={'recruitment-summary_icon recruitment-summary_icon-supply'}
        src={'/img/icons/supply.png'}
        alt={'Снабжение'}
        title={'Снабжение на всех рабочих фермах игрока'}
      />
      <span className={'recruitment-summary_value text-supply'}>{workingFarmsSupply}</span>
      </div>
    </GamePanel>
  );
};

export default RecruitmentSummary;
